import React, { useEffect, useRef, useState } from 'react';
import { IMAGES } from '../../assets/marketing/manifest';
import { focal } from '../content/images';
import type { Pillar } from '../content/pillars';
import useInView from '../hooks/useInView';
import useReducedMotion from '../hooks/useReducedMotion';
import Picture from '../primitives/Picture';

interface PillarListProps {
  pillars: Pillar[];
  label: string;
  /** Auto-advance interval; off under reduced motion, off after the first interaction. */
  autoCycleMs?: number;
}

const FADE_MS = 520;

/**
 * "YOUR HEALTH, MANAGED." (guidelines p.54): the eight pillars as a list at
 * display size, the active one black and the rest Mineral, with a pair of
 * photographs on the right that crossfade to match. Hover, focus, click and
 * the arrow keys choose; when nobody is interacting the list walks itself
 * slowly, unless the visitor prefers reduced motion.
 */
export default function PillarList({ pillars, label, autoCycleMs = 4000 }: PillarListProps) {
  const [active, setActive] = useState(0);
  const [leaving, setLeaving] = useState<number | null>(null);
  const [interacted, setInteracted] = useState(false);
  const [hover, setHover] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const reduced = useReducedMotion();
  const inView = useInView(rootRef, { threshold: 0.3 });
  const count = pillars.length;

  const choose = (index: number, byUser: boolean) => {
    if (index === active) return;
    setLeaving(active);
    setActive(index);
    if (byUser) setInteracted(true);
  };

  // Drop the outgoing layer once its fade has finished.
  useEffect(() => {
    if (leaving === null) return undefined;
    const timer = setTimeout(() => setLeaving(null), reduced ? 0 : FADE_MS);
    return () => clearTimeout(timer);
  }, [leaving, reduced]);

  // Auto-cycle: only while visible, untouched, un-hovered, unfocused and the tab is in front.
  useEffect(() => {
    if (reduced || interacted || !inView || hover || focusWithin) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      setActive((current) => {
        setLeaving(current);
        return (current + 1) % count;
      });
    }, autoCycleMs);
    return () => clearInterval(id);
  }, [reduced, interacted, inView, hover, focusWithin, autoCycleMs, count]);

  // Warm the next pair so the crossfade never waits on the network.
  useEffect(() => {
    if (typeof Image === 'undefined') return;
    const next = pillars[(active + 1) % count];
    next.images.forEach((id) => {
      const img = new Image();
      img.src = IMAGES[id].webp[0].src;
    });
  }, [active, count, pillars]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    const map: Record<string, number> = {
      ArrowDown: active + 1,
      ArrowUp: active - 1,
      Home: 0,
      End: count - 1,
    };
    const next = map[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const index = (next + count) % count;
    choose(index, true);
    itemRefs.current[index]?.focus();
  };

  const layers = [active, leaving].filter((i): i is number => i !== null && i !== undefined);

  return (
    <div
      ref={rootRef}
      className="mk-pillars"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocusWithin(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusWithin(false);
      }}
    >
      <div>
        <p className="mk-pillars__label t-details">{label}</p>
        <ul className="mk-pillars__list" onKeyDown={onKeyDown}>
          {pillars.map((pillar, index) => (
            <li key={pillar.id}>
              <button
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                type="button"
                className="mk-pillars__item t-pillar"
                aria-current={index === active ? 'true' : undefined}
                tabIndex={index === active ? 0 : -1}
                onMouseEnter={() => choose(index, true)}
                onFocus={() => choose(index, true)}
                onClick={() => choose(index, true)}
              >
                {pillar.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="mk-pillars__media" aria-hidden="true">
        {layers.map((index) => {
          const pillar = pillars[index];
          return (
            <div key={pillar.id} className="mk-pillars__layer" data-active={index === active ? 'true' : 'false'}>
              <Picture
                image={IMAGES[pillar.images[0]]}
                alt=""
                sizes="(min-width: 768px) 38vw, 100vw"
                focal={focal(pillar.images[0])}
                className="mk-picture--main"
                style={{ aspectRatio: 'auto' }}
              />
              <Picture
                image={IMAGES[pillar.images[1]]}
                alt=""
                sizes="(min-width: 768px) 16vw, 0px"
                focal={focal(pillar.images[1])}
                className="mk-picture--aside"
                style={{ aspectRatio: '3 / 4' }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
